import { useRef, useEffect, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@altimateai/lego";
import { ArrowDown } from "@assets/icons";
import { APIFilterDropdown, StaticFilterDropdown } from "@/components/filterDropdown";
import { useAppState } from "@/modules/app/useAppContext";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useExplorerUsersQuery, useOpportunityNamesQuery } from "../../hooks";
import {
  EFFORT_OPTIONS,
  RESOURCE_TYPE_OPTIONS,
  RESOURCE_TYPE_OPTIONS_FREEMIUM,
  STATUS_OPTIONS,
} from "../../constants";
import {
  getOpportunitiesResourceNameFilters,
  getOpportunitiesUserFilters,
  getOpportunitiesWarehouseFilters,
} from "@/helpers/apis";
import { isFreeStack } from "@/helpers/tenant";
import styles from "../../styles.module.scss";

interface FilterOption {
  value: string;
  label: string;
  subComponent: (close: () => void) => React.ReactNode;
}

export const FilterSelectorDropdown = () => {
  const { datastores = [] } = useAppState();
  const { searchParams } = useFiltersBySearchParams();
  const { activeUserList } = useExplorerUsersQuery();
  const { opportunityNames } = useOpportunityNamesQuery();
  const [selectedFilter, setSelectedFilter] = useState<FilterOption | null>(null);
  const [subPopoverOpen, setSubPopoverOpen] = useState(false);
  const [mainPopoverOpen, setMainPopoverOpen] = useState(false);
  const mainPopoverRef = useRef<HTMLDivElement>(null);
  const subPopoverRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hoveringOption, setHoveringOption] = useState<boolean>(false);
  const [hoveringSubPopover, setHoveringSubPopover] = useState(false);

  const FILTER_OPTIONS: FilterOption[] = [
    {
      value: "effort",
      label: "Effort",
      subComponent: close => {
        return (
          <StaticFilterDropdown
            id="effort-filter"
            label={"Effort"}
            value={searchParams.getAll("effort") || []}
            options={EFFORT_OPTIONS}
            onChange={() => {
              close();
            }}
            showSearch={false}
            searchKey="effort"
          />
        );
      },
    },
    {
      value: "resource_type",
      label: "Resource Type",
      subComponent: close => {
        return (
          <StaticFilterDropdown
            id="resource-type-filter"
            label={"Resource Type"}
            value={searchParams.getAll("resource_type") || []}
            options={isFreeStack() ? RESOURCE_TYPE_OPTIONS_FREEMIUM : RESOURCE_TYPE_OPTIONS}
            onChange={() => {
              close();
            }}
            showSearch={false}
            searchKey="resource_type"
          />
        );
      },
    },
    {
      value: "owner",
      label: "Owner",
      subComponent: close => {
        return (
          <StaticFilterDropdown
            id="owner-filter"
            label={"Owner"}
            value={searchParams.getAll("owner") || []}
            options={activeUserList}
            onChange={() => {
              close();
            }}
            searchKey="owner"
          />
        );
      },
    },
    {
      value: "opportunity_name",
      label: "Opportunity Name",
      subComponent: close => {
        return (
          <StaticFilterDropdown
            id="opportunity-name-filter"
            label={"Opportunity Name"}
            value={searchParams.getAll("opportunity_name") || []}
            options={opportunityNames || []}
            onChange={() => {
              close();
            }}
            searchKey="opportunity_name"
          />
        );
      },
    },
    {
      value: "resource_name",
      label: "Resource Name",
      subComponent: close => {
        return (
          <APIFilterDropdown
            id="resource-name-filter"
            label={"Resource Name"}
            value={searchParams.getAll("resource_name") || []}
            apiFunction={getOpportunitiesResourceNameFilters}
            onChange={() => {
              close();
            }}
            seeMore
            searchKey="resource_name"
          />
        );
      },
    },
    {
      value: "status_name",
      label: "Status",
      subComponent: close => {
        return (
          <StaticFilterDropdown
            id="status-filter"
            label={"Status"}
            value={searchParams.getAll("status") || []}
            options={STATUS_OPTIONS}
            onChange={() => {
              close();
            }}
            showSearch={false}
            searchKey="status"
          />
        );
      },
    },
    {
      value: "user",
      label: "User",
      subComponent: close => {
        return (
          <APIFilterDropdown
            id="user-filter"
            label={"User"}
            value={searchParams.getAll("user") || []}
            apiFunction={getOpportunitiesUserFilters}
            onChange={() => {
              close();
            }}
            seeMore
            searchKey="user"
          />
        );
      },
    },
    ...(!isFreeStack()
      ? [
          {
            value: "warehouse",
            label: "Warehouse",
            subComponent: (close: () => void) => {
              return (
                <APIFilterDropdown
                  id="warehouse-filter"
                  label={"Warehouse"}
                  value={searchParams.getAll("warehouse") || []}
                  apiFunction={getOpportunitiesWarehouseFilters}
                  onChange={() => {
                    close();
                  }}
                  seeMore
                  searchKey="warehouse"
                />
              );
            },
          },
          {
            value: "instance",
            label: "Instance",
            subComponent: (close: () => void) => {
              return (
                <StaticFilterDropdown
                  id="instance-filter"
                  label={"Instance"}
                  value={searchParams.getAll("instance") || []}
                  options={datastores.flatMap(d =>
                    d.instance_id ? [{ label: d.name, value: d.instance_id }] : []
                  )}
                  onChange={() => {
                    close();
                  }}
                  showSearch={false}
                  searchKey="instance"
                />
              );
            },
          },
        ]
      : []),
  ];

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Close sub-popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        subPopoverRef.current &&
        !subPopoverRef.current.contains(event.target as Node) &&
        mainPopoverRef.current &&
        !mainPopoverRef.current.contains(event.target as Node)
      ) {
        setSubPopoverOpen(false);
        setMainPopoverOpen(false);
        setSelectedFilter(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle mouse leaving the option
  const handleMouseLeave = () => {
    // Only start the timeout if we're not already hovering the sub-popover
    if (!hoveringSubPopover) {
      // Clear any existing timeout to prevent race conditions
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        // Double-check we're still not hovering either element before closing
        if (!hoveringSubPopover && !hoveringOption) {
          setSubPopoverOpen(false);
          setSelectedFilter(null);
        }
      }, 1500); // Increased delay to 1500ms to give more time to move to the sub-popover
    }
    setHoveringOption(false);
  };

  // Handle mouse entering the sub-popover
  const handleSubPopoverMouseEnter = () => {
    // Clear any pending close timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setHoveringSubPopover(true);
  };

  // Handle mouse leaving the sub-popover
  const handleSubPopoverMouseLeave = () => {
    setHoveringSubPopover(false);

    // Clear any existing timeout to prevent race conditions
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only close if we're not hovering the main option
    if (!hoveringOption) {
      timeoutRef.current = setTimeout(() => {
        // Double-check we're still not hovering either element before closing
        if (!hoveringSubPopover && !hoveringOption) {
          setSubPopoverOpen(false);
          setSelectedFilter(null);
        }
      }, 1000); // Increased delay to 1000ms when leaving the sub-popover
    }
  };

  return (
    <div className={styles.filterSelector} ref={mainPopoverRef}>
      <Popover open={mainPopoverOpen} onOpenChange={setMainPopoverOpen}>
        <PopoverTrigger className={styles.dropdown}>
          <div className={styles.label}>
            Filter By: <span className={styles.selected}></span>
          </div>
          <div className={styles.arrow_down}>
            <ArrowDown />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="al-p-0"
          align="start"
          sideOffset={8}
          style={{ minHeight: "300px", minWidth: "200px", zIndex: 40 }}
        >
          <Command>
            <CommandInput placeholder="Search filters..." className="al-border-none" />
            <CommandList className="al-overflow-auto">
              <CommandEmpty>No filters found.</CommandEmpty>
              <CommandGroup>
                {FILTER_OPTIONS.map(option => (
                  <div key={option.value} className="relative">
                    <Popover open={selectedFilter?.value === option.value && subPopoverOpen}>
                      <PopoverTrigger asChild>
                        <CommandItem
                          onMouseEnter={() => {
                            setSelectedFilter(option);
                            setSubPopoverOpen(true);
                            setHoveringOption(true);
                            // Clear any pending close timeout
                            if (timeoutRef.current) {
                              clearTimeout(timeoutRef.current);
                            }
                          }}
                          onMouseLeave={handleMouseLeave}
                          className="al-cursor-pointer"
                          data-value={option.value}
                        >
                          {option.label}
                        </CommandItem>
                      </PopoverTrigger>
                      <PopoverContent
                        ref={subPopoverRef}
                        className={styles.subPopoverContainer}
                        align="start"
                        side="left"
                        sideOffset={8}
                        onMouseEnter={handleSubPopoverMouseEnter}
                        onMouseLeave={handleSubPopoverMouseLeave}
                        style={{
                          backgroundColor: "white",
                          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                          border: "1px solid #e7e8ea",
                          borderRadius: "8px",
                          padding: "16px",
                          zIndex: 3000,
                          minWidth: "300px",
                          maxHeight: "90vh",
                          overflow: "auto",
                        }}
                      >
                        {option.subComponent(() => {
                          setSubPopoverOpen(false);
                          setMainPopoverOpen(false);
                        })}
                      </PopoverContent>
                    </Popover>
                  </div>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
